import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { approvalsAPI } from '../api/client';
import { extractErrorMessage } from '../utils/errors';
import RejectModal from './RejectModal';
import ApprovalProgressModal from './ApprovalProgressModal';

/**
 * "There is a gate here waiting on you" -- with the two buttons, on the page
 * where the approver is actually reading the thing (GitHub #39).
 *
 * Before this, the only place to approve was the queue, so an approver who
 * opened a document to read it had to navigate back and find the row again.
 *
 * The row is fetched from the server (GET /api/approvals/pending filtered by
 * document), never handed over through location.state from the queue: a
 * reload, a bookmark or a link pasted to a colleague has to behave the same as
 * a click-through, and the queue endpoint is already scoped to the caller's own
 * active committee memberships, so asking it is also the authorization answer.
 * No match -- the common case, for anyone who is not an approver on an open
 * gate -- renders nothing at all.
 *
 * `revisionId` picks between a document's own gate and the gate on a proposed
 * revision; null means the document itself. `afterActionTo` is where to go once
 * the decision is recorded -- the queue, when that is where the approver came
 * from; otherwise the page just refreshes and the bar disappears, which is the
 * confirmation.
 */
function ApprovalActionBar({ documentId, revisionId = null, onActed, afterActionTo = null }) {
  const navigate = useNavigate();
  const [pending, setPending] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [progressOpen, setProgressOpen] = useState(false);

  const fetchPending = useCallback(async () => {
    try {
      const res = await approvalsAPI.getPending({ document_id: documentId });
      // A document can have at most one open gate, but it may be the document's
      // own or a revision's -- match on which one this page is showing. Both
      // sides normalised, since one is a route param (a string) and the other
      // comes back as a number or null.
      const wanted = revisionId == null ? '' : String(revisionId);
      const match = res.data.find(
        (row) => String(row.document_revision_id ?? '') === wanted
      );
      setPending(match || null);
    } catch (err) {
      // Non-fatal and deliberately silent: not being able to load this must
      // never stop the page rendering the document itself.
      console.error('Error loading pending approval:', err);
      setPending(null);
    }
  }, [documentId, revisionId]);

  useEffect(() => {
    fetchPending();
  }, [fetchPending]);

  const afterAction = async () => {
    setError(null);
    if (afterActionTo) {
      navigate(afterActionTo);
      return;
    }
    await fetchPending();
    if (onActed) onActed();
  };

  const handleApprove = async () => {
    setBusy(true);
    try {
      // No member_id: the backend derives the acting member from the session.
      // A request does not get to nominate who it is acting as.
      await approvalsAPI.approve(pending.id, {});
      await afterAction();
    } catch (err) {
      console.error('Error approving:', err);
      setError(extractErrorMessage(err));
      // Re-read: on an ALL gate this may be "you already approved", in which
      // case the bar should now show the updated progress rather than the same
      // button that just failed.
      fetchPending();
    } finally {
      setBusy(false);
    }
  };

  const handleRejectSave = async (data) => {
    try {
      await approvalsAPI.reject(pending.id, data);
      setRejecting(false);
      await afterAction();
    } catch (err) {
      console.error('Error rejecting:', err);
      setError(extractErrorMessage(err));
      setRejecting(false);
      fetchPending();
    }
  };

  if (!pending) return null;

  // Server-computed by the same function approve_step calls to decide
  // completion (workflow_service.step_progress) -- never re-derived from
  // actions_so_far here, which is deliberately widened to every cycle of
  // this gate (issue #221).
  const approvedCount = pending.approved_member_ids.length;

  return (
    <div className="alert alert-info approval-action-bar" role="status">
      <div>
        <strong>
          {pending.acting_on_behalf_of_name
            ? `Awaiting approval from ${pending.acting_on_behalf_of_name}`
            : 'Awaiting your approval'}
        </strong>
        {' — '}
        Gate {pending.step_order} of {pending.total_steps}: {pending.group_name}
        {/* Set only when this gate is reachable through a delegation (issue
            #44). Approving here is recorded against the person named -- their
            seat, your hands -- so the bar must say so before the buttons. */}
        {pending.acting_on_behalf_of_name && (
          <span
            className="badge"
            style={{ marginLeft: '8px', background: 'var(--info-bg)', color: 'var(--info-ink)' }}
            title={`You hold this gate through a delegation from ${pending.acting_on_behalf_of_name}`}
          >
            🤝 on behalf of {pending.acting_on_behalf_of_name}
          </span>
        )}
        {pending.type_evaluation_score != null && (
          // Same score already fetched for this row's queue entry -- no
          // second API call. is_stub is not called out here (unlike the full
          // panel): a coloured percentage next to the buttons is a nudge, not
          // a claim, and the full TypeEvaluationPanel below states the
          // caveat explicitly for anyone who opens it.
          <span
            className="badge"
            style={{ marginLeft: '8px', background: 'var(--neutral-bg)', color: 'var(--neutral-ink)' }}
            title="Document-type match score"
          >
            🏷️ {Math.round(pending.type_evaluation_score * 100)}%
          </span>
        )}
        {pending.corpus_duplicate_count > 0 && (
          // "This document has N near-duplicates in the library" -- a fact
          // about the document, carrying no title and no id, so it needs no
          // scope filtering. Naming the matches is CorpusScanPanel's job,
          // where per-reader redaction applies.
          <span
            className="badge"
            style={{ marginLeft: '8px', background: 'var(--warning-bg)', color: 'var(--warning-ink)' }}
            title="Documents already in the library that closely resemble this one"
          >
            ⧉ {pending.corpus_duplicate_count} similar
          </span>
        )}
        <div style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '2px' }}>
          <button type="button" className="btn-link" onClick={() => setProgressOpen(true)}>
            {pending.completion_rule === 'all'
              ? `All must approve — ${approvedCount} of ${pending.members.length} so far`
              : `Any one approver — ${approvedCount} of ${pending.members.length} so far`}
          </button>
          {pending.viewer_has_approved && (
            <span style={{ marginLeft: '8px', color: 'var(--success-ink)' }}>✓ You approved</span>
          )}
          {pending.viewer_has_rejected && (
            <span style={{ marginLeft: '8px', color: 'var(--danger-ink)' }}>↩ You returned this</span>
          )}
        </div>
        {error && <div style={{ marginTop: '6px', color: 'var(--danger-ink)' }}>{error}</div>}
      </div>

      <div className="approval-action-bar-buttons">
        <button className="btn btn-success btn-small" onClick={handleApprove} disabled={busy}>
          ✓ Approve
        </button>
        <button
          className="btn btn-danger btn-small"
          onClick={() => setRejecting(true)}
          disabled={busy}
        >
          ✗ Reject
        </button>
      </div>

      {/* The same modal the queue uses, unmodified: the send-back rules
          (change category, author-only vs previous-gate) live there and in
          approvals.py::reject_step, never in two copies. */}
      {rejecting && (
        <RejectModal
          approval={pending}
          onSave={handleRejectSave}
          onClose={() => setRejecting(false)}
        />
      )}

      {progressOpen && (
        <ApprovalProgressModal
          approval={pending}
          onClose={() => setProgressOpen(false)}
        />
      )}
    </div>
  );
}

export default ApprovalActionBar;
